import React from 'react';
import { Icon } from './ui/Icon';

interface StatCardProps {
  title: string;
  value: string | number;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  colorType?: 'positive' | 'negative' | 'neutral';
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Enhanced StatCard Component - A reusable card for displaying statistics with icons, trends, and animations
 */
const StatCard: React.FC<StatCardProps> = ({ 
  title, 
  value, 
  icon, 
  trend, 
  trendValue, 
  colorType = 'neutral',
  className = '',
  style = {}
}) => {
  // Enhanced color mapping for professional SaaS aesthetic
  const iconColorClasses = {
    positive: 'text-emerald-600',
    negative: 'text-rose-600',
    neutral: 'text-gray-600'
  };

  const iconBgClasses = {
    positive: 'bg-emerald-50',
    negative: 'bg-rose-50',
    neutral: 'bg-gray-50'
  };

  const trendColorClasses = {
    up: 'text-emerald-600',
    down: 'text-rose-600',
    neutral: 'text-gray-600'
  };

  const iconColorClass = iconColorClasses[colorType];
  const iconBgClass = iconBgClasses[colorType];
  const trendColorClass = trend ? trendColorClasses[trend] : trendColorClasses.neutral;

  return (
    <div 
      className={`
        rounded-2xl border bg-card shadow-sm p-5 sm:p-6 min-h-[132px] 
        transition-all duration-200 hover:shadow-md
        ${className}
      `}
      style={style}
      role="article"
      aria-labelledby={`stat-${title.replace(/\s+/g, '-').toLowerCase()}`}
    >
      {/* Card Header - Icon and Label Row */}
      <div className="flex items-center justify-between">
        <span 
          id={`stat-${title.replace(/\s+/g, '-').toLowerCase()}`}
          className="text-sm font-medium text-muted-foreground"
        >
          {title}
        </span>
        {icon && (
          <div 
            className={`size-8 rounded-xl grid place-items-center ${iconBgClass}`}
            aria-hidden="true"
          >
            <Icon 
              icon={icon} 
              size="sm" 
              className={`w-4 h-4 ${iconColorClass}`}
            />
          </div>
        )}
      </div>
      
      {/* Card Body - Main Value */}
      <div className="mt-3">
        <p 
          className="text-3xl font-semibold tracking-tight"
          aria-label={`${title}: ${value}`}
        >
          {value}
        </p>
      </div>
      
      {/* Card Footer - Trend indicator */}
      {trend && trendValue && (
        <div className="mt-2 flex items-center gap-2 text-xs">
          <span className={trendColorClass}>
            {trend === 'up' ? '↗' : trend === 'down' ? '↘' : '→'}
          </span>
          <span className="text-muted-foreground">
            {trendValue} from last 24 hours
          </span>
        </div>
      )}
    </div>
  );
};

export default StatCard;